import { html, render, type TemplateResult } from 'lit-html';
import type { UiConfig } from './auth.js';

/** viewer reads everything; admin also manages keys, connectors, templates and mail; owner also secrets and people. */
export type Role = 'viewer' | 'admin' | 'owner';
export const ROLE_LABEL: Record<Role, string> = { viewer: 'Viewer', admin: 'Admin', owner: 'Owner' };

export interface Me {
  email: string;
  role: Role;
  auth: 'sso' | 'dev';
  stage: string;
  force_test_mode: boolean;
  public_url: string;
}

export interface Page {
  title: string;
  /** Loads data for the page; called on navigation and after actions. `param` is the rest of the route. */
  load(param: string): Promise<void>;
  view(): TemplateResult;
}

export const app = {
  config: null as unknown as UiConfig,
  me: null as unknown as Me,
  route: 'overview',
  routeParam: '',
  loading: false,
  error: null as string | null,
  toasts: [] as { id: number; text: string; tone: string }[],
  modal: null as null | (() => TemplateResult),
  confirm: null as null | {
    title: string;
    body: TemplateResult | string;
    confirmLabel: string;
    tone: 'danger' | 'primary';
    resolve: (ok: boolean) => void;
  },
  rerender: () => undefined as void,
};

export const isAdmin = () => app.me?.role === 'admin' || app.me?.role === 'owner';
export const isOwner = () => app.me?.role === 'owner';

let toastSeq = 0;
export function toast(text: string, tone: 'positive' | 'critical' | 'info' = 'positive'): void {
  const id = ++toastSeq;
  app.toasts.push({ id, text, tone });
  app.rerender();
  setTimeout(
    () => {
      app.toasts = app.toasts.filter((t) => t.id !== id);
      app.rerender();
    },
    tone === 'critical' ? 9000 : 4000,
  );
}

export function confirmDialog(opts: { title: string; body: TemplateResult | string; confirmLabel: string; tone?: 'danger' | 'primary' }): Promise<boolean> {
  return new Promise((resolve) => {
    app.confirm = { ...opts, tone: opts.tone ?? 'primary', resolve };
    app.rerender();
  });
}

export function openModal(view: () => TemplateResult): void {
  app.modal = view;
  app.rerender();
}
export function closeModal(): void {
  app.modal = null;
  app.rerender();
}

/** Runs an action, reports its outcome as a toast, and reloads. Returns the action's result, or null on failure. */
export async function act<T>(fn: () => Promise<T>, success: string | null, reload?: () => Promise<void>): Promise<T | null> {
  try {
    const r = await fn();
    if (success) toast(success);
    if (reload) await reload();
    app.rerender();
    return r;
  } catch (err) {
    toast((err as Error).message, 'critical');
    return null;
  }
}

export function formValues(form: HTMLFormElement): Record<string, string> {
  const out: Record<string, string> = {};
  new FormData(form).forEach((v, k) => {
    out[k] = String(v);
  });
  return out;
}

export function mount(root: HTMLElement, view: () => TemplateResult): void {
  app.rerender = () => render(view(), root);
}

export function emptyRow(cols: number, text: string): TemplateResult {
  return html`<tr><td colspan=${cols} class="empty">${text}</td></tr>`;
}

export async function copy(text: string, what = 'Copied'): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast(what);
  } catch {
    toast('Could not copy. Select the text and copy it by hand.', 'critical');
  }
}
